import { useEffect, useMemo, useState } from 'react'
import { Bug } from 'lucide-react'
import { ListTable } from '@/components/ui/ListTable'
import {
  SortableTh,
  TH_CLASS,
  nextSort,
  type SortState,
} from '@/components/ui/SortableTh'
import {
  TABLE_PAGE_SIZE,
  TablePagination,
} from '@/components/ui/TablePagination'
import { IssueRow } from '../shared/IssueRow'
import { useResettablePage } from '../../../occurrences/hooks/useResettablePage'
import type { CountLabel } from './triage-overview'
import type {
  SystemIssue,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../../types/system-issues'

interface TriageQueueListProps {
  issues: SystemIssue[]
  isLoading: boolean
  onSelect: (issueId: number) => void
  /** Coluna "Reportado por" — some em "meus problemas" (é sempre a pessoa). */
  showReporterMeta?: boolean
  /** Dev: prioridade editável e "Atribuir a…" na linha (desligado por
   * padrão — quem chama decide). */
  canEditPriority?: boolean
  /** Muda quando o recorte muda (filtros, pílula, card): volta à 1ª página.
   * A lista recarregar (socket, atribuição na linha) NÃO volta. */
  resetKey?: string
  /** Aba HUB do processo — ver `IssueRow.compact`. */
  compact?: boolean
  /** Rótulo do rodapé, [singular, plural]; padrão chamado/chamados. */
  countLabel?: CountLabel
  emptyText?: string
}

type SortKey = 'status' | 'priority' | 'created_at' | 'last_message_at'

// Ordem de negócio (não alfabética): o 1º clique em asc segue esta sequência.
const STATUS_RANK: Record<SystemIssueStatus, number> = {
  open: 0,
  pending: 1,
  in_progress: 2,
  improvement_suggestion: 3,
  resolved: 4,
}
const PRIORITY_RANK: Record<SystemIssuePriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
}

// Mesmo valor exibido nas colunas (a "última mensagem" cai na abertura).
const SORT_VALUE: Record<SortKey, (issue: SystemIssue) => number> = {
  status: (i) => STATUS_RANK[i.status],
  priority: (i) => PRIORITY_RANK[i.priority],
  created_at: (i) => Date.parse(i.created_at),
  last_message_at: (i) => Date.parse(i.last_message_at ?? i.created_at),
}
const SORT_LABEL: Record<SortKey, string> = {
  status: 'pelo status',
  priority: 'pela prioridade',
  created_at: 'pela abertura',
  last_message_at: 'pela última mensagem',
}
const DEFAULT_COUNT_LABEL: CountLabel = ['chamado', 'chamados']

/** Sem coluna escolhida: a conversa mais recente primeiro. */
const DEFAULT_SORT: SortState<SortKey> = { key: 'last_message_at', dir: 'desc' }

/** Tabela de chamados do Atendimento HUB (fila, "meus problemas", aba HUB
 * do processo). */
export function TriageQueueList({
  issues,
  isLoading,
  onSelect,
  showReporterMeta = true,
  canEditPriority = false,
  compact = false,
  resetKey = '',
  countLabel = DEFAULT_COUNT_LABEL,
  emptyText = 'Nenhum chamado por aqui.',
}: TriageQueueListProps) {
  const [sort, setSort] = useState<SortState<SortKey> | null>(null)
  const [page, setPaginaAtual] = useResettablePage(`${resetKey}|${sort?.key}|${sort?.dir}`)
  // Lista encolheu: a página fica na última que existe — gravada, para não
  // "voltar" à antiga se a lista crescer de novo (socket).
  const lastPage = Math.max(1, Math.ceil(issues.length / TABLE_PAGE_SIZE))
  const paginaAtual = Math.min(page, lastPage)
  useEffect(() => {
    if (!isLoading && page > lastPage) setPaginaAtual(lastPage)
  }, [isLoading, page, lastPage, setPaginaAtual])

  const toggleSort = (key: SortKey) => setSort((s) => nextSort(s, key))
  const effective = sort ?? DEFAULT_SORT

  const sortedIssues = useMemo(() => {
    const sign = effective.dir === 'asc' ? 1 : -1
    const value = SORT_VALUE[effective.key]
    return [...issues].sort((a, b) => (value(a) - value(b)) * sign)
  }, [issues, effective.key, effective.dir])

  const startIndex = (paginaAtual - 1) * TABLE_PAGE_SIZE
  const issuesPaginadas = useMemo(
    () => sortedIssues.slice(startIndex, startIndex + TABLE_PAGE_SIZE),
    [sortedIssues, startIndex],
  )

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border-subtle bg-surface p-8 text-center text-sm text-subtle">
        Carregando…
      </div>
    )
  }

  if (issues.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-border-subtle bg-surface p-8 text-center text-sm text-subtle">
        <Bug className="h-8 w-8 opacity-40" />
        {emptyText}
      </div>
    )
  }

  // Faixa da página só quando há mais de uma ("13–15 de 15 chamados …").
  const pageRange =
    issues.length > TABLE_PAGE_SIZE
      ? `${startIndex + 1}–${Math.min(startIndex + TABLE_PAGE_SIZE, issues.length)} de `
      : ''

  const sortable = (label: string, key: SortKey) => (
    <SortableTh
      label={label}
      dir={sort?.key === key ? sort.dir : null}
      onToggle={() => toggleSort(key)}
    />
  )

  return (
    <ListTable
      head={
        <>
          <th className="w-10 py-3.5 pl-5 pr-0">
            <span className="sr-only">Não lida</span>
          </th>
          <th className={TH_CLASS}>Chamado</th>
          {showReporterMeta && !compact && <th className={TH_CLASS}>Reportado por</th>}
          <th className={TH_CLASS}>Atribuído a</th>
          {sortable('Status', 'status')}
          {sortable('Prioridade', 'priority')}
          {!compact && sortable('Aberto', 'created_at')}
          {sortable('Última mensagem', 'last_message_at')}
        </>
      }
      footer={
        <TablePagination
          page={paginaAtual}
          pageSize={TABLE_PAGE_SIZE}
          total={issues.length}
          onPageChange={setPaginaAtual}
          itemLabel={countLabel[1]}
          summary={`${pageRange}${issues.length} ${countLabel[issues.length === 1 ? 0 : 1]} · ${
            issues.length === 1 ? 'ordenado' : 'ordenados'
          } ${SORT_LABEL[effective.key]}`}
        />
      }
    >
      {issuesPaginadas.map((issue) => (
        <IssueRow
          key={issue.id}
          issue={issue}
          onSelect={onSelect}
          showReporterMeta={showReporterMeta}
          canEditPriority={canEditPriority}
          compact={compact}
        />
      ))}
    </ListTable>
  )
}
